import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../../components/loading-spinner/LoadingSpinner";

const MetaCallback = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [tokens, setTokens] = useState(null);

  const hasRun = React.useRef(false);

  useEffect(() => {
    if (hasRun.current) return; // prevent double-invocation in StrictMode
    hasRun.current = true;

    const exchangeCode = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");

      if (!code) {
        return navigate("/channels?error=no-code");
      }

      try {
        const redirectUri = `${window.location.origin}/auth/meta/callback`;
        const userId =
          user?.userId ||
          user?.username ||
          user?.sub ||
          localStorage.getItem("mb_userId");

        if (!userId) {
          console.warn("[MetaCallback] No userId found – aborting token exchange");
          return navigate("/channels?error=no-user");
        }

        const endpointBase = `${process.env.REACT_APP_API_URL}/auth`;
        const resp = await axios.post(`${endpointBase}/meta/exchange-code`, {
          code,
          redirectUri,
          userId,
        });

        setTokens(resp.data.tokens);
        setAccounts(resp.data.adAccounts || []);
        setLoading(false);
      } catch (err) {
        console.error("Meta token exchange failed:", err);
        const data = err.response?.data;
        if (data?.missing?.length) {
          const granted = data.granted?.length ? ` Granted: ${data.granted.join(", ")}` : "";
          setError(`Missing required Meta permissions: ${data.missing.join(", ")}.${granted}`);
        } else {
          setError(data?.error || "Token exchange failed");
        }
        setLoading(false);
      }
    };

    exchangeCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = async (adAccountId) => {
    if (!tokens) return;
    try {
      setLoading(true);
      const endpointBase = `${process.env.REACT_APP_API_URL}/auth`;
      const userId =
        user?.userId ||
        user?.username ||
        user?.sub ||
        localStorage.getItem("mb_userId");

      await axios.post(`${endpointBase}/meta/complete-auth`, {
        userId,
        adAccountId,
        ...tokens,
      });

      navigate("/channels?connected=meta");
    } catch (err) {
      console.error("Failed to complete Meta auth:", err);
      setError(err.response?.data?.error || "Failed to complete authentication");
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center text-red-500">
        {error}
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="mb-4 text-xl font-bold">Select your Meta Ad Account</h2>
      {accounts.length === 0 ? (
        <p>No Meta ad accounts were found for your user.</p>
      ) : (
        <ul className="space-y-2">
          {accounts.map((acc) => (
            <li key={acc.id}>
              <button
                className="w-full rounded border px-4 py-2 text-left hover:bg-accent"
                onClick={() => handleSelect(acc.accountId || acc.id)}
              >
                {acc.name || acc.accountId || acc.id}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MetaCallback;
